/**
 * hls.js 缓存拦截 Loader（阿里云盘视频「边播边存 / 命中即免流量」）
 *
 * 工作方式（路径 B，不改写 m3u8）：
 * - 分片(.ts/.m4s)请求：先用「归一化路径」查 IndexedDB；
 *   命中 → 直接把缓存字节回调给 hls.js（0 网络、0 流量）；
 *   未命中 → 交给 hls.js 默认 loader 走 CDN，成功后顺手写入缓存。
 * - level m3u8(媒体清单)请求：正常走网络，成功后解析出该档全部分片路径，
 *   存为 manifest，用于「整档分片齐全才标已缓存」的完整性判定。
 * - master/manifest：不处理，原样放行。
 *
 * 该 loader 仅在传入有效 cacheCtx(含 videoId)时启用缓存；否则等价于默认 loader。
 */

import {
  normalizeSegmentUrl,
  buildSegmentKey,
  buildManifestKey,
  getSegment,
  putSegment,
  putManifest
} from '@/services/videoSegmentCache.js';

/** 解析 media playlist 文本，提取分片绝对 URL 列表（按出现顺序） */
function parsePlaylistSegmentUrls(playlistText, playlistUrl) {
  if (!playlistText || typeof playlistText !== 'string') return [];
  const urls = [];
  const lines = playlistText.split(/\r?\n/);
  for (let raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue; // 跳过空行与标签
    // 非标签行即为分片 URI（可能相对/绝对）
    try {
      const abs = new URL(line, playlistUrl).href;
      urls.push(abs);
    } catch (_) {
      // 解析失败则跳过该行
    }
  }
  return urls;
}

/** 判断 loader context 是否为「分片」请求（通过 URL 特征和 context 判断） */
function isFullFragmentContext(context) {
  if (!context) return false;
  
  // 1) 有 frag 字段的标准分片
  if (context.frag && !context.part) {
    // byte-range 分片：缓存局部字节会破坏完整性，跳过
    // 注意：rangeStart=0 rangeEnd=0 是 hls.js 默认值，不算 byte-range
    const hasValidByteRange =
      (typeof context.rangeStart === 'number' && typeof context.rangeEnd === 'number'
       && (context.rangeStart > 0 || context.rangeEnd > 0)) ||
      (context.frag.byteRange && context.frag.byteRange.length > 0);
    console.log('[isFullFragmentContext] frag分支: hasValidByteRange=', hasValidByteRange, 
                'rangeStart=', context.rangeStart, 'rangeEnd=', context.rangeEnd, 
                'frag.byteRange=', context.frag.byteRange);
    if (hasValidByteRange) return false; // byte-range 局部分片跳过
    return true;
  }
  
  // 2) URL 特征判断：常见分片扩展名，且不是 m3u8
  const url = context.url;
  if (typeof url === 'string') {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('.m3u8')) return false; // 排除 m3u8
    // 分片常见扩展名或路径特征（阿里云视频分片通常无扩展名，靠路径判断）
    if (/\.(ts|m4s|mp4|m4v|m4a)(\?|$)/i.test(url)) return true;
    // 阿里云视频分片 URL 通常形如 /qv/{hash}_{seqNum}?params，非 m3u8 且有查询参数
    if (/\/qv\/[^\/]+_\d+\?/.test(url)) return true;
  }
  
  return false;
}

/** 是否为 media playlist（含分片清单）请求 */
function isLevelPlaylistContext(context) {
  return context && (context.type === 'level' || context.type === 'audioTrack');
}

function nowMs() {
  return (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : Date.now();
}

/** 归一化分片 URL；blob manifest 场景下用 levelKey 反推绝对 https 路径 */
function resolveSegmentNormalized(url, ctx) {
  const direct = normalizeSegmentUrl(url);
  if (direct) return direct;
  if (!url?.startsWith('blob:') || !ctx?.levelKey) return null;
  try {
    const blobUrl = new URL(url);
    const relative = `${blobUrl.pathname.replace(/^\//, '')}${blobUrl.search || ''}`;
    const baseDir = new URL('.', `https://${ctx.levelKey}`).href;
    return normalizeSegmentUrl(new URL(relative, baseDir).href);
  } catch (_) {
    return null;
  }
}

/**
 * 构建缓存 loader 类。
 * @param {Function} BaseLoader hls.js 默认 loader 类（Hls.DefaultConfig.loader）
 * @param {{ videoId:(string|number), enabled?:boolean, logPrefix?:string }} cacheCtx
 * @returns {Function} loader 类
 */
export function createHlsCacheLoader(BaseLoader, cacheCtx) {
  const ctx = cacheCtx || {};
  const enabled = ctx.enabled !== false && ctx.videoId != null;
  const logPrefix = ctx.logPrefix || '[HLS缓存]';

  return class HlsCacheLoader extends BaseLoader {
    /**
     * hls.js 会复用同一 loader 实例。abort() 后若不重置，
     * 后续 load 在 IndexedDB 命中后仍会被「请求已中止」静默丢弃，图集二次播放会一直卡在加载中。
     */
    _beginLoad() {
      this._aborted = false;
      this._loadToken = (this._loadToken || 0) + 1;
      return this._loadToken;
    }

    _isLoadStale(loadToken) {
      return this._aborted || this._loadToken !== loadToken;
    }

    load(context, config, callbacks) {
      const loadToken = this._beginLoad();
      const url = context && context.url;
      console.log(`${logPrefix} load 被调用, enabled=${enabled}, context.type=${context?.type}, url=${url}`);
      
      if (!enabled) {
        console.log(`${logPrefix} 缓存未启用，使用默认 loader`);
        return super.load(context, config, callbacks);
      }

      // ---- 分片请求：命中缓存直接返回；未命中走网络并回写 ----
      if (isFullFragmentContext(context) && url) {
        console.log(`${logPrefix} 检测到分片请求:`, url);
        const normalized = resolveSegmentNormalized(url, ctx);
        if (normalized) {
          const segKey = buildSegmentKey(ctx.videoId, normalized);
          console.log(`${logPrefix} 分片缓存键:`, segKey);
          this._loadFragmentWithCache(segKey, context, config, callbacks, loadToken);
          return;
        } else {
          console.warn(`${logPrefix} 分片 URL 归一化失败，回退默认 loader:`, url);
        }
      }

      // ---- media playlist：网络加载成功后解析分片清单存 manifest ----
      if (isLevelPlaylistContext(context) && url) {
        console.log(`${logPrefix} 检测到 level playlist 请求:`, url);
        this._loadPlaylistAndIndex(context, config, callbacks, loadToken);
        return;
      }

      // 其余（master 等）原样放行
      console.log(`${logPrefix} 其他请求类型 (type=${context?.type}, frag=${!!context?.frag}, url=${url?.substring(0,150)})，使用默认 loader`);
      return super.load(context, config, callbacks);
    }

    _loadFragmentWithCache(segKey, context, config, callbacks, loadToken) {
      const url = context.url;
      const startTime = nowMs();
      console.log(`${logPrefix} _loadFragmentWithCache 开始，segKey=${segKey.substring(0, 120)}`);

      getSegment(segKey).then((cached) => {
        console.log(`${logPrefix} getSegment 完成，cached=${cached ? `${cached.byteLength}B` : 'null'}`);
        if (this._isLoadStale(loadToken)) {
          console.log(`${logPrefix} 请求已中止，跳过`);
          return;
        }

        if (cached && cached.byteLength > 0) {
          // 命中缓存：构造 stats 直接回调，不走网络
          console.log(`${logPrefix} ✅ 命中缓存分片(${cached.byteLength}B):`, segKey.substring(0, 100));
          const end = nowMs();
          const len = cached.byteLength;
          const stats = this.stats || {};
          stats.aborted = false;
          stats.loaded = len;
          stats.total = len;
          stats.retry = 0;
          stats.chunkCount = stats.chunkCount || 0;
          stats.bwEstimate = stats.bwEstimate || 0;
          stats.loading = { start: startTime, first: startTime, end };
          stats.parsing = stats.parsing || { start: 0, end: 0 };
          stats.buffering = stats.buffering || { start: 0, first: 0, end: 0 };

          // 返回缓存的副本（hls.js 开启 worker 时会 transfer/detach 数据）
          let data;
          try {
            data = cached.slice(0);
          } catch (_) {
            data = cached;
          }
          const response = { url, data };
          try {
            // eslint-disable-next-line no-console
            if (ctx.debug) console.log(`${logPrefix} 命中缓存分片(${len}B):`, segKey);
          } catch (_) { /* ignore */ }

          // 异步回调，贴合 hls.js 期望的事件循环时序
          setTimeout(() => {
            if (this._isLoadStale(loadToken)) return;
            callbacks.onSuccess(response, stats, context, null);
          }, 0);
          return;
        }

        // 未命中：走默认 loader，成功后回写缓存
        console.log(`${logPrefix} 未命中缓存，走网络加载`);
        if (this._isLoadStale(loadToken)) return;
        const wrapped = Object.assign({}, callbacks, {
          onSuccess: (response, stats, ctx2, networkDetails) => {
            console.log(`${logPrefix} 网络加载成功，准备写入缓存`);
            try {
              const data = response && response.data;
              if (data && data.byteLength > 0) {
                console.log(`${logPrefix} 写入缓存分片(${data.byteLength}B):`, segKey.substring(0, 100));
                // 先复制再回写，避免 worker transfer 后 buffer detached
                let copy;
                try {
                  copy = data.slice(0);
                } catch (_) {
                  copy = null;
                }
                if (copy) {
                  putSegment(segKey, copy).catch((e) => {
                    console.warn(`${logPrefix} 写入缓存失败:`, e);
                  });
                }
              }
            } catch (e) {
              console.warn(`${logPrefix} 回写缓存异常:`, e);
            }
            callbacks.onSuccess(response, stats, ctx2, networkDetails);
          }
        });
        super.load(context, config, wrapped);
      }).catch((e) => {
        console.error(`${logPrefix} getSegment 异常:`, e);
        // 查询缓存异常：退回普通网络加载
        if (this._isLoadStale(loadToken)) return;
        super.load(context, config, callbacks);
      });
    }

    _loadPlaylistAndIndex(context, config, callbacks, loadToken) {
      const playlistUrl = context.url;
      console.log(`${logPrefix} 加载 level playlist:`, playlistUrl);

      // 🚀 优先查询 m3u8 缓存，命中即离线播放（0 网络请求）
      const levelKey = normalizeSegmentUrl(playlistUrl) || String(playlistUrl);
      import('@/services/videoSegmentCache.js')
        .then(m => m.getPlaylist(ctx.videoId, levelKey))
        .then(cachedContent => {
          if (cachedContent && !this._isLoadStale(loadToken)) {
            console.log(`${logPrefix} ✅ 命中 m3u8 缓存，离线播放`);
            // 构造缓存响应，模拟网络加载成功
            const response = { url: playlistUrl, data: cachedContent };
            const stats = this.stats || {};
            stats.aborted = false;
            stats.loaded = cachedContent.length;
            stats.total = cachedContent.length;
            setTimeout(() => {
              if (this._isLoadStale(loadToken)) return;
              callbacks.onSuccess(response, stats, context, null);
            }, 0);
            return true; // 标记已处理
          }
          return false; // 未命中，继续网络加载
        })
        .then(handled => {
          if (handled || this._isLoadStale(loadToken)) return;

          // 未命中缓存：走网络加载，成功后同时缓存 m3u8 文本和分片清单
          const wrapped = Object.assign({}, callbacks, {
            onSuccess: (response, stats, ctx2, networkDetails) => {
              try {
                const text = response && response.data;
                if (typeof text === 'string' && text.indexOf('#EXTINF') !== -1) {
                  // 缓存 m3u8 文本（用于下次离线播放）
                  import('@/services/videoSegmentCache.js')
                    .then((m) => m.putPlaylist(
                      ctx.videoId,
                      levelKey,
                      m.rewritePlaylistWithAbsoluteUrls(text, levelKey)
                    ))
                    .catch(() => {});

                  // 解析分片 URL，缓存清单索引
                  const segUrls = parsePlaylistSegmentUrls(text, playlistUrl);
                  if (segUrls.length > 0) {
                    const segmentKeys = [];
                    for (const su of segUrls) {
                      const norm = normalizeSegmentUrl(su);
                      if (norm) segmentKeys.push(buildSegmentKey(ctx.videoId, norm));
                    }
                    if (segmentKeys.length > 0) {
                      const manifestKey = buildManifestKey(ctx.videoId, levelKey);
                      putManifest(manifestKey, {
                        videoId: ctx.videoId,
                        levelKey,
                        segmentKeys,
                        total: segmentKeys.length,
                        updatedAt: Date.now()
                      }).catch(() => { /* ignore */ });
                    }
                  }
                }
              } catch (_) { /* 解析清单失败不影响播放 */ }
              callbacks.onSuccess(response, stats, ctx2, networkDetails);
            }
          });

          super.load(context, config, wrapped);
        })
        .catch(err => {
          console.warn(`${logPrefix} m3u8 缓存查询异常，回退网络加载:`, err);
          if (this._isLoadStale(loadToken)) return;
          super.load(context, config, callbacks);
        });
    }

    abort() {
      this._aborted = true;
      this._loadToken = (this._loadToken || 0) + 1;
      super.abort();
    }

    destroy() {
      this._aborted = true;
      this._loadToken = (this._loadToken || 0) + 1;
      super.destroy();
    }
  };
}

export default createHlsCacheLoader;
